import React from 'react';
import PropTypes from 'prop-types';
import { strings } from '../../../../localization';
import lsUtil from '../../../../components/common/lsUtil';
import SimpleSideBySideNav from '../../../../components/common/SimpleSideBySideNav';
import {
    MAX_SCORE_TREND_PREVIOUS_PERIODS,
    timePeriodShortLabelForDate } from './../SafetyScoreModuleGroup';
import ProgressOverlay from '../../../../components/common/ProgressOverlay';
import ResizeThrottler from '../../../../components/ResizeThrottler';
import { SUPPORTED_TIME_PERIOD_TYPES, REPORT_BY_TYPES, colorForSafetyCategory } from './../SafetyScore';
import { NOT_APPLICABLE_SCORE, SUPPORTED_SCORE_CATEGORIES } from './../SafetyScore';
import {
    Chart,
    Geom,
    Coord,
    Label,
    View,
    Guide
} from "bizcharts";
import DataSet from "@antv/data-set";
const { Text } = Guide;
const { DataView } = DataSet;

const IDEAL_HEIGHT_FOR_WIDTH_375 = 301; // a magic number determined by comparing the horizontal and vertical dimensions of the chart's labels (horizontal affected by max width of strings, vertical is simply line height)
const IDEAL_MARGIN_TOP_OFFSET_FOR_WIDTH_375 = -50;
const IDEAL_HORIZONTAL_LABEL_WIDTH_FOR_WIDTH_375 = 112;
const IDEAL_VERTICAL_LABEL_HEIGHT_FOR_WIDTH_375 = 22;
const IDEAL_INNER_RADIUS_FOR_WIDTH_375 = 0.65;
const CHART_LABEL_OFFSET = 23; // loosely corresponds to length of label's guideline
const IDEAL_SCORE_FONT_SIZE_FOR_WIDTH_375 = 45;
const IDEAL_SCORE_LABEL_FONT_SIZE_FOR_WIDTH_375 = 13;
const IDEAL_CHART_BREAKDOWN_LABEL_FONT_SIZE_FOR_WIDTH_375 = 11;

const RESIZE_EVENT_HANDLER_THROTTLE_INTERVAL_MS = 100;

export default class ScoreSummary extends React.PureComponent {

    static propTypes = {
        totalTimePeriodsLoaded: PropTypes.number.isRequired,
        selectedTimePeriod: PropTypes.number.isRequired,
        onChangeSelectedTimePeriod: PropTypes.func.isRequired,
        selectedTimePeriodType: PropTypes.string.isRequired,
        periodStartDate: PropTypes.instanceOf(Date),
        periodEndDate: PropTypes.instanceOf(Date),
        selectedReportBy: PropTypes.string.isRequired,
        selectedScoreThresholds: PropTypes.object,
        score: PropTypes.number.isRequired,
        scoreSummariesByEntityId: PropTypes.object.isRequired,
        previousPeriodScore: PropTypes.number
    };

    constructor(props) {
        super(props);

        this.chartContainerRef = React.createRef();

        this.state = {
            parentDivWidth: 0,
            dynamicHeight: 0,
            dynamicScoreValueYAxisPercent: 0,
            dynamicScoreCategoryLabelYAxisPercent: 0
        };
    }

    updateChartDimensions = () => {

        if (this.chartContainerRef.current) {
            const parentDivWidth = this.chartContainerRef.current.offsetWidth;
            const dynamicHeight = IDEAL_HEIGHT_FOR_WIDTH_375  * (parentDivWidth / 375);
            const dynamicScoreValueYAxisPercent = 48;
            const dynamicScoreCategoryLabelYAxisPercent = 57;

            if (this.state.parentDivWidth !== parentDivWidth ||
                this.state.dynamicHeight !== dynamicHeight ||
                this.state.dynamicScoreValueYAxisPercent !== dynamicScoreValueYAxisPercent ||
                this.state.dynamicScoreCategoryLabelYAxisPercent !== dynamicScoreCategoryLabelYAxisPercent) {
                this.setState({
                    parentDivWidth,
                    dynamicHeight,
                    dynamicScoreValueYAxisPercent,
                    dynamicScoreCategoryLabelYAxisPercent
                });
            }
        }
    }

    componentDidMount() {
        this.updateChartDimensions();
    }

    componentDidUpdate(prevProps) {
        this.updateChartDimensions();
    }

    calculateScoreCategoryPercentages = () => {

        if (!this.props.scoreSummariesByEntityId) {
            console.warn("ScoreSummary.calculateScoreCategoryPercentages: scoreSummariesByEntityId is not defined");
            return [];
        }

        if (!this.props.selectedScoreThresholds) {
            console.warn("ScoreSummary.calculateScoreCategoryPercentages: selectedScoreThresholds is not defined");
            return [];
        }

        let lowRiskCount = 0;
        let mildRiskCount = 0;
        let mediumRiskCount = 0;
        let highRiskCount = 0;
        let totalEntities = 0;

        for (const entityId in this.props.scoreSummariesByEntityId) {
            const scoreSummary = this.props.scoreSummariesByEntityId[entityId];
            if (scoreSummary.score > this.props.selectedScoreThresholds[SUPPORTED_SCORE_CATEGORIES.LOW]) {
                lowRiskCount++;
            } else if (scoreSummary.score > this.props.selectedScoreThresholds[SUPPORTED_SCORE_CATEGORIES.MILD]) {
                mildRiskCount++;
            } else if (scoreSummary.score > this.props.selectedScoreThresholds[SUPPORTED_SCORE_CATEGORIES.MEDIUM]) {
                mediumRiskCount++;
            } else if (scoreSummary.score === NOT_APPLICABLE_SCORE) {
                continue; // omit this entity from the total count and percentages
            } else {
                highRiskCount++;
            }

            totalEntities++;
        }

        const lowRiskPercentage = Math.round(lowRiskCount / totalEntities * 100);
        const mildRiskPercentage = Math.round(mildRiskCount / totalEntities * 100);
        const mediumRiskPercentage = Math.round(mediumRiskCount / totalEntities * 100);
        const highRiskPercentage = Math.round(highRiskCount / totalEntities * 100);

        const userDataSet = [];
        // only add data points for categories that have a percentage greater than 0
        if (lowRiskPercentage > 0) {
            userDataSet.push({
                label: lowRiskPercentage + "% " + strings.score_risk_label_low_compact,
                type: SUPPORTED_SCORE_CATEGORIES.LOW,
                value: lowRiskPercentage
            });
        }
        if (mildRiskPercentage > 0) {
            userDataSet.push({
                label: mildRiskPercentage + "% " + strings.score_risk_label_mild_compact,
                type: SUPPORTED_SCORE_CATEGORIES.MILD,
                value: mildRiskPercentage
            });
        }
        if (mediumRiskPercentage > 0) {
            userDataSet.push({
                label: mediumRiskPercentage + "% " + strings.score_risk_label_medium_compact,
                type: SUPPORTED_SCORE_CATEGORIES.MEDIUM,
                value: mediumRiskPercentage
            });
        }
        if (highRiskPercentage > 0) {
            userDataSet.push({
                label: highRiskPercentage + "% " + strings.score_risk_label_high_compact,
                type: SUPPORTED_SCORE_CATEGORIES.HIGH,
                value: highRiskPercentage
            });
        }
        return userDataSet;
    }

    typeColorJSXexpression = ["type", colorForSafetyCategory];

    scorePercentagesDataView = (scoreSummariesByEntityId) => {
        const scorePercentagesByType = this.calculateScoreCategoryPercentages(scoreSummariesByEntityId);
        const scorePercentagesDataView = new DataView();
        scorePercentagesDataView.source(scorePercentagesByType).transform({
            type: "percent",
            field: "value",
            dimension: "type",
            as: "percent"
        });
        return scorePercentagesDataView;
    }

    scoreCategoryForValue = (value) => {
        if (value > this.props.selectedScoreThresholds[SUPPORTED_SCORE_CATEGORIES.LOW]) {
            return SUPPORTED_SCORE_CATEGORIES.LOW;
        } else if (value > this.props.selectedScoreThresholds[SUPPORTED_SCORE_CATEGORIES.MILD]) {
            return SUPPORTED_SCORE_CATEGORIES.MILD;
        } else if (value > this.props.selectedScoreThresholds[SUPPORTED_SCORE_CATEGORIES.MEDIUM]) {
            return SUPPORTED_SCORE_CATEGORIES.MEDIUM;
        } else if (value === NOT_APPLICABLE_SCORE) {
            console.warn("ScoreSummary.scoreCategoryForValue: requesting a category for a NOT_APPLICABLE_SCORE");
            return; // return undefined
        } else {
            return SUPPORTED_SCORE_CATEGORIES.HIGH;
        }
    }

    currentIdealWidthConversionFactor = () => {
        return (this.state.parentDivWidth / 375);
    }

    onTimePeriodClickNext = (event) => {
        let selectedTimePeriod = this.props.selectedTimePeriod - 1; // time periods are in reverse order (0 is current, 1+ is historical)
        if (selectedTimePeriod < 0) {
            selectedTimePeriod = MAX_SCORE_TREND_PREVIOUS_PERIODS;
        }
        this.props.onChangeSelectedTimePeriod(selectedTimePeriod);
    }

    onTimePeriodClickPrevious = (event) => {
        let selectedTimePeriod = this.props.selectedTimePeriod + 1; // time periods are in reverse order (0 is current, 1+ is historical)
        if (selectedTimePeriod > MAX_SCORE_TREND_PREVIOUS_PERIODS) {
            selectedTimePeriod = 0;
        }
        this.props.onChangeSelectedTimePeriod(selectedTimePeriod);
    }

    render() {

        const periodStartDateString = timePeriodShortLabelForDate(this.props.periodStartDate);
        let periodEndDateString;

        const scorePercentagesDataView = this.scorePercentagesDataView(this.props.scoreSummariesByEntityId);
        let scoreDelta;
        let scoreDeltaString;

        if (this.props.previousPeriodScore !== undefined && this.props.previousPeriodScore !== NOT_APPLICABLE_SCORE) {
            if (this.props.score !== undefined && this.props.score !== NOT_APPLICABLE_SCORE) {
                scoreDelta = Math.round(this.props.score - this.props.previousPeriodScore);
                if (scoreDelta !== 0) {
                    scoreDeltaString = scoreDelta > 0 ? "↑" : "↓";
                    const scoreDeltaMagnitude = Math.abs(scoreDelta);
                    scoreDeltaString += scoreDeltaMagnitude + " "; // note I'm adding a space at the end
                    if (scoreDeltaMagnitude > 1) {
                        scoreDeltaString += strings.score_points_label;
                    } else {
                        scoreDeltaString += strings.score_points_label_singular;
                    }
                }
            }
        }

        if (this.props.selectedTimePeriod === 0) {
            periodEndDateString = strings.score_time_period_present;

            switch (this.props.selectedTimePeriodType) {
                case SUPPORTED_TIME_PERIOD_TYPES.WEEKLY:
                    periodEndDateString += " (" + strings.score_time_period_current_week + ")";
                    break;
                case SUPPORTED_TIME_PERIOD_TYPES.MONTHLY:
                    periodEndDateString += " (" + strings.score_time_period_current_month + ")";
                    break;
                case SUPPORTED_TIME_PERIOD_TYPES.QUARTERLY:
                    periodEndDateString += " (" + strings.score_time_period_current_quarter + ")";
                    break;
                default:
                    break;
            }
        } else {
            periodEndDateString = timePeriodShortLabelForDate(this.props.periodEndDate);
        }

        const idealWidthConversionFactor = this.currentIdealWidthConversionFactor();

        const headerElement = (
            <header className="score-header">
                <h6>
                    <span>
                        {this.props.selectedReportBy === REPORT_BY_TYPES.DRIVER ? strings.score_summary_header_for_drivers : strings.score_summary_header_for_vehicles}
                    </span>
                    {scoreDelta !== undefined &&
                        <span className={scoreDelta >= 0 ? "score-delta positive" : "score-delta negative"}>
                            {scoreDelta !== 0 &&
                                scoreDeltaString
                            }
                        </span>
                    }
                    <br />
                    {periodStartDateString && periodEndDateString &&
                        <div className="time-period">
                            <SimpleSideBySideNav
                                middleElement={<span>{periodStartDateString + " - " + periodEndDateString}</span>}
                                heightStyle="16px"
                                iconWidthStyle="8px"
                                leftDisabled={this.props.selectedTimePeriod === MAX_SCORE_TREND_PREVIOUS_PERIODS}
                                rightDisabled={this.props.selectedTimePeriod === 0}
                                onLeftClick={this.onTimePeriodClickPrevious}
                                onRightClick={this.onTimePeriodClickNext}
                            />
                        </div>
                    }
                </h6>
            </header>);

        if (this.props.score === NOT_APPLICABLE_SCORE) {
            return (
                <section className="score-summary" ref={this.chartContainerRef} >
                    {headerElement}
                    <div className="insufficient-data">
                        {strings.score_insufficient_data}
                    </div>
                </section>
            );
        }

        let selectedTheme = lsUtil.getItem('raven.theme') || 'light';
        const scoreTextColor = selectedTheme === "light" ? "#000000" : "#FFFFFF";


        return (
            <section className="score-summary" ref={this.chartContainerRef} >
                {headerElement}
                <div style={{marginTop: String(IDEAL_MARGIN_TOP_OFFSET_FOR_WIDTH_375 * idealWidthConversionFactor) + "px"}}>
                    <Chart
                        width={this.state.parentDivWidth}
                        height={this.state.dynamicHeight}
                        padding={[
                            IDEAL_VERTICAL_LABEL_HEIGHT_FOR_WIDTH_375 * idealWidthConversionFactor,
                            IDEAL_HORIZONTAL_LABEL_WIDTH_FOR_WIDTH_375 * idealWidthConversionFactor,
                            IDEAL_VERTICAL_LABEL_HEIGHT_FOR_WIDTH_375 * idealWidthConversionFactor,
                            IDEAL_HORIZONTAL_LABEL_WIDTH_FOR_WIDTH_375 * idealWidthConversionFactor
                        ]}
                        forceFit
                    >
                        <View data={scorePercentagesDataView}>
                            <Coord type="theta" innerRadius={(IDEAL_INNER_RADIUS_FOR_WIDTH_375 * idealWidthConversionFactor)} />{/* corresponds to thickness of circle */}
                            <Geom
                                type="intervalStack"
                                position="percent"
                                color={this.typeColorJSXexpression}
                            >
                                <Label
                                    content="label"
                                    offset={CHART_LABEL_OFFSET}
                                    textStyle={{
                                        fontSize: IDEAL_CHART_BREAKDOWN_LABEL_FONT_SIZE_FOR_WIDTH_375 * idealWidthConversionFactor
                                    }}
                                />{/* CHART_LABEL_OFFSET corresponds to length of label's guideline */}
                            </Geom>
                            {this.props.totalTimePeriodsLoaded &&
                                <Guide>
                                    {/* fleet score value in centre of circle */}
                                    <Text
                                        position={[
                                            "50%", // aligns text horizontally in the centre of the circle
                                            this.state.dynamicScoreValueYAxisPercent + "%"]}
                                        content={ String(Math.round(this.props.score))}
                                        style={{
                                            font: "Roboto, sans-serif",
                                            fontSize: String(IDEAL_SCORE_FONT_SIZE_FOR_WIDTH_375 * idealWidthConversionFactor),
                                            fontWeight: "900",
                                            fill: scoreTextColor,
                                            textAlign: "center"
                                        }}
                                    />
                                    {/* fleet score's risk category label under the score value text */}
                                    <Text
                                        position={[
                                            "50%", // aligns text horizontally in the centre of the circle
                                            this.state.dynamicScoreCategoryLabelYAxisPercent + "%"]}
                                        content={strings["score_risk_label_" + this.scoreCategoryForValue(this.props.score).toLocaleLowerCase()]}
                                        style={{
                                            fontSize: String(IDEAL_SCORE_LABEL_FONT_SIZE_FOR_WIDTH_375 * idealWidthConversionFactor),
                                            fontWeight: "lighter",
                                            fill: scoreTextColor,
                                            textAlign: "center"
                                        }}
                                    />
                                </Guide>
                            }
                        </View>
                    </Chart>
                </div>
                {this.props.totalTimePeriodsLoaded &&
                    <div className="score-summary-legend" style={{marginTop: String((IDEAL_MARGIN_TOP_OFFSET_FOR_WIDTH_375 / 2) * idealWidthConversionFactor) + "px"}}>
                        {this.props.selectedScoreThresholds && Object.keys(this.props.selectedScoreThresholds).map((scoreCategory, index) => {
                            return (
                                <div key={index} className="legend-item">
                                    <div className="legend-color" style={{ backgroundColor: colorForSafetyCategory(scoreCategory) }}></div>
                                    <div className="legend-label">
                                        {strings["score_risk_label_" + scoreCategory.toLowerCase()]}
                                        <br/>
                                        <>{" > " + this.props.selectedScoreThresholds[scoreCategory]}</>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                }
                {this.props.totalTimePeriodsLoaded === 0 &&
                    <ProgressOverlay />
                }
                <ResizeThrottler interval={RESIZE_EVENT_HANDLER_THROTTLE_INTERVAL_MS} callback={this.updateChartDimensions} />
            </section>
        );
    }
}